import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { CartsDataViewContext, getCartsDataView } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCarts from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCarts";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, {
    DataTableHeaderPresentationProps,
    DataTableHeaderProps,
} from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getDataLayerUtilityV4 } from "@tCentro/Custom/Utilities/getDataLayerUtility";
import CenArrowRightIcon from "@tCentro/Icons/CenArrowRightIcon";
import { COLORS } from "@tCentro/theme/design";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
    };
}
const mapStateToProps = (state: ApplicationState) => ({
    getSavedOrdersParameter: state.pages.savedOrderList.getCartsApiParameter,
    language: state.context.session.language,
    savedOrderDetailsLink: getPageLinkByPageType(state, "SavedOrderDetailsPage"),
    savedOrdersPageLink: getPageLinkByPageType(state, "SavedOrderListPage"),
    showPricing: state.context.showPricing,
    savedCartsDataView: getCartsDataView(state, state.pages.savedOrderList.getCartsApiParameter),
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
    loadCarts,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface SavedOrderListTableStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    orderDateHeader?: DataTableHeaderProps;
    shipToLabelHeader?: DataTableHeaderProps;
    subtotalHeader?: DataTableHeaderProps;
    orderDateCell?: DataTableCellBaseProps;
    shipToLabelCell?: DataTableCellBaseProps;
    subtotalCell?: DataTableCellBaseProps;
    headingMainContainer?: InjectableCss;
    leftContentStyles?: InjectableCss;
    headingStyles?: InjectableCss;
    allOrderLinkStyles?: LinkPresentationProps;
    imgCells?: DataTableCellProps;
    countStyles?: InjectableCss;
    imgStyles?: InjectableCss;
    imgContainer?: InjectableCss;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    noSavedOrderStyles?: InjectableCss;
}

export const savedOrderListTableStyles: SavedOrderListTableStyles = {
    noSavedOrderStyles: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
            text-align: center;
        `,
    },
    noOrdersFoundText: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            text-align: center;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    dataTableRow: {
        css: css`
            td {
                vertical-align: top;
                padding-top: 8px;
            }
        `,
    },
    imgContainer: {
        css: css`
            width: 64px;
            height: 64px;
            aspect-ratio: 1/1;
            background-color: white;
            box-shadow: 0px 2px 8px 0px rgba(38, 37, 37, 0.16);
        `,
    },
    imgStyles: {
        css: css`
            width: 64px !important;
            height: 64px !important;
            object-fit: contain;
        `,
    },
    dataTable: {
        css: css`
            th {
                background: none;
                span {
                    font-family: Poppins;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 16px;
                    text-align: left;
                    color: ${getProp("theme.typography.p.color")};
                }
            }

            tbody {
                tr {
                    &:nth-child(odd) {
                        background: #f2f2f2;
                    }
                    &:nth-child(even) {
                        background: #ffffff;
                    }
                }
            }

            td {
                border-bottom: none;
                span {
                    font-family: Poppins;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 16px;
                    text-align: left;
                    color: ${getProp("theme.typography.p.color")};
                }
            }
        `,
    },
    headingStyles: {
        css: css`
            font-family: Poppins;
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            text-align: left;
            color: ${getProp("theme.typography.p.color")};
            text-transform: uppercase;
        `,
    },
    leftContentStyles: {
        css: css`
            display: flex;
            gap: 24px;
            justify-content: flex-start;
            align-items: center;
        `,
    },
    headingMainContainer: {
        css: css`
            justify-content: space-between;
            width: 100%;
            display: flex;
            align-items: center;
            padding: 28px 15px 15px 15px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        gap: 24px;
                        align-items: flex-start;
                    `,
                    css``,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    container: {
        css: css`
            padding: 0px 12px 0px 12px;
            overflow-y: scroll;
            max-height: 384px;
            ::-webkit-scrollbar {
                width: 6px; /* Width of the vertical scrollbar */
                height: 6px; /* Height of the horizontal scrollbar */
            }

            ::-webkit-scrollbar-track {
                background: #ffffff; /* Background of the scrollbar track */
                border-radius: 3px; /* Rounded corners for the track */
            }

            ::-webkit-scrollbar-thumb {
                background: #e0dfe0; /* Color of the scrollbar thumb */
                border-radius: 3px; /* Rounded corners for the thumb */
            }

            ::-webkit-scrollbar-thumb:hover {
                background: #555; /* Color of the thumb on hover */
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-left: 8px;
                        padding-right: 8px;
                    `,
                    css`
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    subtotalHeader: {
        css: css`
            width: 50%;
        `,
    },
    subtotalCell: {},
    allOrderLinkStyles: {
        css: css`
            margin-left: auto;
            margin-top: 5px;
            border: 2px solid ${getColor("secondary")};
            padding: 8px 12px 8px 16px;
            flex-direction: row-reverse;
            gap: 4px;
            border-radius: 16px;
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            span:nth-child(2) {
                color: ${getColor("secondary")} !important;
            }
        `,
    },
    imgCells: {
        css: css`
            padding: 8px 0px 24px 16px !important;
            display: flex;
            height: 100%;
            width: 100%;
            gap: 7px;
            align-items: center;
        `,
    },
    noOrdersFoundGridItem: {
        width: 12,
        css: css`
            background-color: #f2f2f2;
            margin: 32px 0px !important;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            gap: 4px;
            min-height: 154px;
        `,
    },
};

const styles = savedOrderListTableStyles;

const CENMyAccountSavedOrderListTable = ({
    getSavedOrdersParameter,
    updateSearchFields,
    language,
    savedOrderDetailsLink,
    showPricing,
    savedCartsDataView,
    loadCarts,
    getCartsApiParameter,
    savedOrdersPageLink,
    fields,
}: Props) => {
    React.useEffect(() => {
        if (!savedCartsDataView.value && !savedCartsDataView.isLoading) {
            loadCarts({ apiParameter: getCartsApiParameter });
        }
    });

    if (!savedCartsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    const rows = savedCartsDataView.value.map(savedOrder => {
        return {
            id: savedOrder.id,
            date: savedOrder.orderDate
                ? getLocalizedDateTime({
                    dateTime: new Date(savedOrder.orderDate),
                    language,
                })
                : "",
            savedOrderproductImage: savedOrder?.properties?.productImage,
            savedOrdertProductUri: savedOrder?.properties?.productUri,
            remainingProductCount: savedOrder?.properties?.remainingProductCount,
            shipToLabel:
                savedOrder.fulfillmentMethod === "PickUp" && savedOrder.defaultWarehouse
                    ? savedOrder.defaultWarehouse.description || savedOrder.defaultWarehouse.name
                    : (savedOrder.collectionShipToLabel ?? savedOrder.shipToLabel),
            subtotal: savedOrder.orderSubTotalDisplay,
            properties: savedOrder?.properties
        };
    });

    return (
        <StyledWrapper>
            <StyledWrapper
                css={css`
                    height: 16px;
                    background: ${fields.backgroundColor};
                `}
            ></StyledWrapper>
            <StyledWrapper
                css={css`
                    border: 4px solid #f2f2f2;
                    height: 100%;
                `}
            >
                {savedCartsDataView?.value?.length === 0 && (
                    <StyledWrapper
                        css={css`
                            padding: 32px 24px 0px 24px;
                        `}
                    >
                        <Typography className="card-list-heading" {...styles.headingStyles}>
                            {translate("Saved Orders")}
                        </Typography>
                        <GridItem {...styles.noOrdersFoundGridItem}>
                            <Typography {...styles.noOrdersFoundText}>
                                {translate("You don’t have any saved orders.")}
                            </Typography>
                            <Typography {...styles.noSavedOrderStyles}>
                                {translate("Quickly reorder repeat purchases by creating saved orders.")}
                            </Typography>
                        </GridItem>
                    </StyledWrapper>
                )}
                {savedCartsDataView?.value?.length > 0 && (
                    <>
                        <StyledWrapper {...styles.headingMainContainer}>
                            <StyledWrapper {...styles.leftContentStyles}>
                                <StyledWrapper>
                                    <Typography className="card-list-heading" {...styles.headingStyles}>
                                        {translate("Saved Orders")}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                            {savedOrdersPageLink?.url && (
                                <StyledWrapper>
                                    <Link
                                        {...styles.allOrderLinkStyles}
                                        href={savedOrdersPageLink?.url}
                                        icon={{
                                            iconProps: {
                                                src: CenArrowRightIcon,
                                                size: 16,
                                                css: css`
                                                    svg {
                                                        > path {
                                                            fill: ${getColor("secondary")};
                                                        }
                                                    }
                                                `,
                                            },
                                        }}
                                        typographyProps={{ size: 15 }}
                                        className="view-all-link"
                                        data-test-selector="cardListHeadingViewAllLink"
                                    >
                                        {translate("All Saved Orders")}
                                    </Link>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.container}>
                            <DataTable {...styles.dataTable}>
                                <DataTableHead>
                                    <DataTableHeader {...styles.shipToLabelHeader}>
                                        {translate("Items Saved")}
                                    </DataTableHeader>
                                    <DataTableHeader {...styles.orderDateHeader}>{translate("Date")}</DataTableHeader>
                                    <DataTableHeader {...styles.shipToLabelHeader}>
                                        {translate("Ship To / Pick Up")}
                                    </DataTableHeader>
                                    {showPricing && (
                                        <DataTableHeader {...styles.subtotalHeader}>
                                            {translate("Order Subtotal")}
                                        </DataTableHeader>
                                    )}
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody}>
                                    {rows.map(
                                        ({
                                            id,
                                            savedOrderproductImage,
                                            savedOrdertProductUri,
                                            remainingProductCount,
                                            date,
                                            shipToLabel,
                                            subtotal,
                                            properties
                                        }) => {

                                            const onClickHandler = () => {

                                                const price = parseFloat(properties?.firstProductPrice || "0").toFixed(2);
                                                const value = parseFloat(properties?.firstProductSubTotal || "0").toFixed(2);

                                                const dataLayerObject = {
                                                    "item_id": properties?.firstProductMPN || "",
                                                    "item_name": properties?.firstProductTitle || "",
                                                    "item_brand": properties?.firstProductBrand || "",
                                                    "price": Number(price),
                                                    "quantity": Number(properties?.firstProductQtyOrdered || "1"),
                                                    "value": Number(value),
                                                    "item_list_id": "my_account_saved_orders",
                                                    "item_list_name": "My Account: Saved Orders"
                                                }
                                                dataLayerObject && getDataLayerUtilityV4(dataLayerObject)
                                            }

                                            return (
                                                <DataTableRow
                                                    data-test-selector="savedOrderList_orderLine"
                                                    data-test-key={id}
                                                    key={id}
                                                    {...styles.dataTableRow}
                                                >
                                                    <DataTableCell {...styles.imgCells}>
                                                        <StyledWrapper {...styles.imgContainer}>
                                                            <Link
                                                                href={savedOrdertProductUri}
                                                                css={css`
                                                                width: 100%;
                                                                height: 100%;
                                                                > span {
                                                                    width: 100%;
                                                                    height: 100%;
                                                                }
                                                            `}
                                                                onClick={onClickHandler}
                                                            >
                                                                <Img src={savedOrderproductImage} {...styles.imgStyles} />
                                                            </Link>
                                                        </StyledWrapper>
                                                        {Number(remainingProductCount) > 1 && (
                                                            <Typography as="span" {...styles.countStyles}>
                                                                +{remainingProductCount}
                                                            </Typography>
                                                        )}
                                                    </DataTableCell>
                                                    <DataTableCell {...styles.orderDateCell}>
                                                        <Link href={`${savedOrderDetailsLink?.url}?cartId=${id}`}>
                                                            {date}
                                                        </Link>
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.shipToLabelCell}
                                                        data-test-selector="savedOrderLine_shipTo"
                                                    >
                                                        {shipToLabel}
                                                    </DataTableCell>
                                                    {showPricing && (
                                                        <DataTableCell alignX="left" {...styles.subtotalCell}>
                                                            {subtotal}
                                                        </DataTableCell>
                                                    )}
                                                </DataTableRow>
                                            )
                                        },
                                    )}
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    </>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENMyAccountSavedOrderListTable),
    definition: {
        group: "Saved Order List",
        displayName: "Cen My Account Saved Orders",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(90deg,#266b40 27.08%,#2d804c 71.87%,#43687a 100%)",
                fieldType: "General",
                displayName: "Background Color",
            },
        ],
    },
};

export default widgetModule;
